import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useWebsiteSection } from '../hooks/useWebsiteData';
import { useState } from 'react';
import { ImageModal } from '../components/ImageModal';
import { GridPortfolio } from '../components/GridPortfolio';
import { HeroImage } from '../components/HeroImage';
import { Footer } from '@components/Footer';
import FormExample from '@components/FormExample';
import { useGoldUnderline, GoldUnderlineSpan, CHARCOAL } from '../hooks/useGoldUnderline';

function BackButton({ onClick }: { onClick: () => void }) {
  const { elRef, lineRef } = useGoldUnderline<HTMLButtonElement>();

  return (
    <button
      ref={elRef}
      type="button"
      onClick={onClick}
      className="relative w-auto uppercase tracking-[4px] font-extralight font-sans py-3"
      style={{ color: CHARCOAL }}
    >
      ← Back to All Projects
      <GoldUnderlineSpan lineRef={lineRef} />
    </button>
  );
}

export const Route = createFileRoute('/portfolio/$portfolioId')({
  component: PortfolioProject,
});

function PortfolioProject() {
  const { portfolioId } = Route.useParams();
  const { data: section, isLoading, error } = useWebsiteSection(portfolioId);
  const navigate = useNavigate();

  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    alt: string;
  } | null>(null);

  if (isLoading) {
    return <div className="p-8">Loading {portfolioId}...</div>;
  }

  if (!section) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Portfolio Not Found</h1>
        <p className="mb-4">The portfolio "{portfolioId}" does not exist.</p>
        <Link to="/portfolio" className="text-blue-600 hover:text-blue-800">
          ← Back to all portfolios
        </Link>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <h1 className="text-2xl font-bold mb-4">Error</h1>
        <p className="mb-4">{error.message}</p>
        <Link to="/portfolio" className="text-blue-600 hover:text-blue-800">
          ← Back to all portfolios
        </Link>
      </div>
    );
  }

  // Sort by context.order if present, else keep existing order
  const sortedImages = [...section._images].sort((a, b) => {
    const aOrder = a.context?.order != null ? parseInt(a.context.order, 10) : Infinity;
    const bOrder = b.context?.order != null ? parseInt(b.context.order, 10) : Infinity;
    return aOrder - bOrder;
  });

  // Pre-compute layout rows to avoid mutating during render
  type Row =
    | { type: 'landscape'; img: typeof sortedImages[0] }
    | { type: 'portrait-pair'; a: typeof sortedImages[0]; b: typeof sortedImages[0] }
    | { type: 'portrait-single'; img: typeof sortedImages[0] };

  const rows: Row[] = [];
  let i = 0;
  while (i < sortedImages.length) {
    const img = sortedImages[i];
    const isLandscape = img.width > img.height;
    if (isLandscape) {
      rows.push({ type: 'landscape', img });
      i++;
    } else {
      const next = sortedImages[i + 1];
      if (next && next.width <= next.height) {
        rows.push({ type: 'portrait-pair', a: img, b: next });
        i += 2;
      } else {
        rows.push({ type: 'portrait-single', img });
        i++;
      }
    }
  }

  return (
    <>
      <HeroImage
        imageUrl={
          (
            section._images.find((img) => img.context?.Hero === 'true') ??
            section._images[0]
          ).secure_url
        }
        title={section._images?.[0]?.metadata?.website ?? section._metadata.name.replace(/_/g, ' ')}
        overlayOpacity={0.2}
      />
      <div className="p-8 max-w-7xl mx-auto">
        <div className="space-y-4">
          {rows.map((row) => {
            if (row.type === 'landscape') {
              return (
                <div key={row.img.public_id} className="w-full">
                  <img
                    src={row.img.secure_url}
                    alt={row.img.public_id}
                    className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage({ url: row.img.secure_url, alt: row.img.public_id })}
                  />
                </div>
              );
            }
            if (row.type === 'portrait-pair') {
              return (
                <div key={row.a.public_id} className="grid grid-cols-2 gap-4">
                  <img
                    src={row.a.secure_url}
                    alt={row.a.public_id}
                    className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage({ url: row.a.secure_url, alt: row.a.public_id })}
                  />
                  <img
                    src={row.b.secure_url}
                    alt={row.b.public_id}
                    className="w-full h-auto cursor-pointer hover:opacity-90 transition-opacity"
                    loading="lazy"
                    onClick={() => setSelectedImage({ url: row.b.secure_url, alt: row.b.public_id })}
                  />
                </div>
              );
            }
            return (
              <div key={row.img.public_id} className="w-full md:w-1/2 mx-auto">
                <img
                  src={row.img.secure_url}
                  alt={row.img.public_id}
                  className="w-full h-auto rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                  loading="lazy"
                  onClick={() => setSelectedImage({ url: row.img.secure_url, alt: row.img.public_id })}
                />
              </div>
            );
          })}
        </div>

        {selectedImage && (
          <ImageModal
            imageUrl={selectedImage.url}
            alt={selectedImage.alt}
            onClose={() => setSelectedImage(null)}
          />
        )}

        <GridPortfolio currentPortfolio={portfolioId} />
        <div className="justify-center items-center flex w-full">
          <BackButton onClick={() => navigate({ to: '/portfolio' })} />
        </div>
      </div>
      <FormExample isLoaded={!!section} />
      <Footer isLoaded={!!section} />
    </>
  );
}

export default PortfolioProject;
